import React, { useState, useMemo } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiGrid, FiLayers, FiList } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';
import ContractDrillDownPanel from './ContractDrillDownPanel';

const REGION_COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#A855F7','#06B6D4','#F97316'];

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const UNIT_COLORS   = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#EF4444','#06B6D4','#84CC16','#F97316','#EC4899','#14B8A6'];

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`;
const fmt  = (n) => Number(n || 0).toLocaleString('en-US');

const REGION_METRICS = [
  { key: 'contract_value',  label: 'Value $', fmt: fmtM },
  { key: 'contract_count',  label: 'Count',   fmt: v => v },
  { key: 'contract_weight', label: 'Weight',  fmt: fmtW },
];

const MAIN_TABS = [
  { key: 'region', label: 'By Region',     icon: FiGrid },
  { key: 'unit',   label: 'By Sales Unit', icon: FiLayers },
  { key: 'table',  label: 'Detail Table',  icon: FiList },
];


const SortableTh = ({ label, sortKey, currentSort, onSort }) => {
  const active = currentSort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none tw-whitespace-nowrap"
    >
      {label}
      {active && <span className="tw-ml-1 tw-text-gray-400">{currentSort.dir === 'desc' ? '▼' : '▲'}</span>}
    </th>
  );
};

SortableTh.propTypes = {
  label: PropTypes.node.isRequired,
  sortKey: PropTypes.string.isRequired,
  currentSort: PropTypes.shape({
    key: PropTypes.string,
    dir: PropTypes.oneOf(['asc', 'desc']),
  }).isRequired,
  onSort: PropTypes.func.isRequired,
};

const ContractSalesRegionChart = ({ data, loading, error, baseParams, unitData, unitLoading }) => {
  const [mainTab,    setMainTab]    = useState('region');
  const [metric,     setMetric]     = useState('contract_value');
  const [drillOpen,  setDrillOpen]  = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillExtra, setDrillExtra] = useState({});
  const [tableSort,  setTableSort]  = useState({ key: 'total_value', dir: 'desc' });

  const regionRows = useMemo(() =>
    [...(data || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0)),
    [data, metric]
  );

  const unitRows = useMemo(() =>
    [...(unitData || [])].sort((a, b) => (b.total_value || 0) - (a.total_value || 0)),
    [unitData]
  );

  const tableRows = useMemo(() => {
    const sorted = [...(unitData || [])].sort((a, b) => {
      const diff = (b[tableSort.key] || 0) - (a[tableSort.key] || 0);
      return tableSort.dir === 'desc' ? diff : -diff;
    });
    return sorted;
  }, [unitData, tableSort]);

  const handleSort = (key) => {
    setTableSort(prev =>
      prev.key === key
        ? { ...prev, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );
  };

  const handleRegionClick = (row) => {
    setDrillTitle(`Region: ${row.region}`);
    setDrillExtra({ region: row.region });
    setDrillOpen(true);
  };

  const totalContracts   = tableRows.reduce((s, r) => s + (r.contract_count  || 0), 0);
  const totalAddendums   = tableRows.reduce((s, r) => s + (r.addendum_count  || 0), 0);
  const totalCtrValue    = tableRows.reduce((s, r) => s + (r.contract_value  || 0), 0);
  const totalAdmValue    = tableRows.reduce((s, r) => s + (r.addendum_value  || 0), 0);
  const totalValue       = tableRows.reduce((s, r) => s + (r.total_value     || 0), 0);
  const totalWeight      = tableRows.reduce((s, r) => s + (r.contract_weight || 0), 0);

  const isLoading = loading || (mainTab !== 'region' && unitLoading);

  if (isLoading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading sales data</p>
    </Card>
  );

  const activeFmt   = (REGION_METRICS.find(m => m.key === metric) || REGION_METRICS[0]).fmt;
  const maxRegion   = Math.max(...regionRows.map(r => r[metric] || 0), 1);
  const maxUnit     = Math.max(...unitRows.map(d => d.total_value || 0), 1);
  const chartH      = 160;
  const barW        = 52;
  const gap         = 76;
  const svgW        = Math.max(regionRows.length * gap, 300);

  const regionOption = {
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    grid: { left: 10, right: 10, top: 4, bottom: 0, containLabel: true },
    tooltip: {
      ...CHART_TOOLTIP,
      formatter: (p) => {
        const item = regionRows[p.dataIndex];
        if (!item) return '';
        const color = REGION_COLORS[p.dataIndex % REGION_COLORS.length];
        return [
          `<b>${escapeHtml(item.region)}</b>`,
          `<span style="color:#2563EB;font-weight:600">${item.contract_count} contracts · ${item.addendum_count || 0} addendums</span>`,
          `<span style="color:${color};font-weight:800;font-size:14px">${escapeHtml(fmtM(item.total_value))}</span>`,
          `<span style="color:#6B7280">Weight: ${escapeHtml(fmtW(item.contract_weight))}</span>`,
          `<span style="color:#60A5FA">Click to drill down</span>`
        ].join('<br/>');
      }
    },
    xAxis: {
      type: 'category',
      data: regionRows.map(item => item.region || ''),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        interval: 0,
        formatter: (value, index) => {
          const item = regionRows[index];
          return `{name|${String(value).slice(0, 10)}}\n{counts|${item?.contract_count ?? 0}C · ${item?.addendum_count || 0}A}`;
        },
        rich: {
          name:   { color: '#374151', fontSize: 10, fontWeight: 600, align: 'center' },
          counts: { color: '#9CA3AF', fontSize: 9, align: 'center', padding: [4, 0, 0, 0] }
        }
      }
    },
    yAxis: { type: 'value', show: false, max: maxRegion },
    series: [
      {
        name: 'metric',
        type: 'bar',
        barWidth: barW,
        cursor: 'pointer',
        itemStyle: { opacity: 0.75, borderRadius: 4 },
        label: {
          show: true,
          position: 'insideTop',
          fontSize: 9,
          fontWeight: 700,
          color: '#ffffff',
          formatter: (p) => {
            const item = regionRows[p.dataIndex];
            const barH = maxRegion > 0 ? ((item?.[metric] || 0) / maxRegion) * chartH : 0;
            return barH > 20 ? String(activeFmt(item?.[metric] || 0)) : '';
          }
        },
        data: regionRows.map((item, i) => ({
          value: item[metric] || 0,
          itemStyle: { color: REGION_COLORS[i % REGION_COLORS.length] }
        }))
      },
      // Addendum-value overlay bar (value metric only), drawn over the main bar.
      ...(metric === 'contract_value' ? [{
        name: 'addendum',
        type: 'bar',
        barWidth: barW,
        barGap: '-100%',
        silent: true,
        z: 3,
        itemStyle: { color: '#F59E0B', opacity: 0.5, borderRadius: 4 },
        tooltip: { show: false },
        data: regionRows.map(item => item.addendum_value || 0)
      }] : [])
    ]
  };

  return (
    <>
      <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-primary-50 tw-shadow-sm">
        {/* Header with main tabs */}
        <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
          <div className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-rounded tw-p-1">
            {MAIN_TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setMainTab(key)}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all tw-cursor-pointer tw-border-0 ${
                  mainTab === key
                    ? 'tw-bg-white tw-text-primary-700 tw-shadow-sm'
                    : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-800'
                }`}
              >
                <Icon size={12} />
                {label}
              </button>
            ))}
          </div>

          {mainTab === 'region' && (
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              {REGION_METRICS.map((opt, idx) => (
                <button
                  key={opt.key}
                  onClick={() => setMetric(opt.key)}
                  className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {mainTab === 'region' && (
            <span className="tw-text-xs tw-text-gray-400">{regionRows.length} regions · Click to drill down</span>
          )}
          {mainTab === 'unit' && (
            <span className="tw-text-xs tw-text-gray-400">{unitRows.length} units</span>
          )}
          {mainTab === 'table' && (
            <span className="tw-text-xs tw-text-gray-400">{tableRows.length} units · Click column to sort</span>
          )}
        </div>

        {/* ── Tab: By Region ── */}
        {mainTab === 'region' && (
          <>
            {regionRows.length === 0 ? (
              <p className="tw-text-gray-400 tw-text-center tw-py-10">No data available</p>
            ) : (
              <div className="tw-p-5 tw-overflow-x-auto">
                <ReactECharts
                  option={regionOption}
                  style={{ width: svgW, height: chartH + 60 }}
                  notMerge
                  onEvents={{
                    click: (params) => {
                      const item = regionRows[params.dataIndex];
                      if (item) handleRegionClick(item);
                    }
                  }}
                />
              </div>
            )}
            <div className="tw-px-5 tw-pb-3 tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-gray-500">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-w-3 tw-h-3 tw-rounded-sm" style={{ background: '#6366F1', opacity: 0.75 }} />
                Contract {REGION_METRICS.find(m => m.key === metric)?.label}
              </div>
              {metric === 'contract_value' && (
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <span className="tw-w-3 tw-h-3 tw-rounded-sm" style={{ background: '#F59E0B', opacity: 0.5 }} />
                  + Addendum
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Tab: By Sales Unit ── */}
        {mainTab === 'unit' && (
          <div className="tw-p-5">
            {unitRows.length === 0 ? (
              <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
            ) : (
              <div className="tw-space-y-2.5 tw-max-h-72 tw-overflow-y-auto tw-pr-1">
                {unitRows.map((item, i) => {
                  const pct    = (item.total_value / maxUnit) * 100;
                  const conPct = item.total_value > 0 ? ((item.contract_value || 0) / item.total_value) * pct : 0;
                  const color  = UNIT_COLORS[i % UNIT_COLORS.length];
                  return (
                    <div key={item.sales_unit_id} className="tw-relative">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                          <span className="tw-w-2 tw-h-2 tw-rounded-xl tw-flex-shrink-0" style={{ backgroundColor: color }} />
                          <span className="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate">{item.unit_name}</span>
                          <span className="tw-text-xs tw-text-gray-400 tw-flex-shrink-0">{item.region}</span>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0 tw-ml-2">
                          <span className="tw-text-xs tw-text-gray-400">
                            {fmt(item.contract_count)}C · {fmt(item.addendum_count || 0)}A
                          </span>
                          <span className="tw-text-xs tw-font-bold tw-text-gray-800">{fmtM(item.total_value)}</span>
                        </div>
                      </div>
                      <div className="tw-h-2 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
                        <div className="tw-h-full tw-rounded-xl tw-flex">
                          <div style={{ width: `${conPct}%`, backgroundColor: color, opacity: 0.85 }} />
                          <div style={{ width: `${pct - conPct}%`, backgroundColor: color, opacity: 0.35 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Detail Table ── */}
        {mainTab === 'table' && (
          <div className="tw-overflow-auto tw-max-h-[460px]">
            <table className="tw-w-full tw-text-xs tw-border-collapse">
              <thead>
                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-8">#</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Unit</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Region</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Type</th>
                  <SortableTh label="Contracts"   sortKey="contract_count"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Addendums"   sortKey="addendum_count"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Contract $"  sortKey="contract_value"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Addendum $"  sortKey="addendum_value"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Total $"     sortKey="total_value"     currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Weight (MT)" sortKey="contract_weight" currentSort={tableSort} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((item, i) => (
                  <tr key={item.sales_unit_id} className={`tw-border-b tw-border-gray-100 ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-blue-50/60 tw-transition-colors`}>
                    <td className="tw-px-3 tw-py-2 tw-text-gray-400 tw-font-mono">{i + 1}</td>
                    <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{item.unit_name}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500">{item.region}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500">{item.type_of_sales}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-blue-700">{fmt(item.contract_count)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600 tw-font-semibold">{fmt(item.addendum_count || 0)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-blue-700">{fmtM(item.contract_value)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right" style={{ color: (item.addendum_value || 0) < 0 ? '#DC2626' : '#D97706', fontWeight: 600 }}>
                      {(item.addendum_value || 0) !== 0 ? fmtM(item.addendum_value) : '—'}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-emerald-700">{fmtM(item.total_value)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(item.contract_weight)}</td>
                  </tr>
                ))}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs" colSpan={4}>Total</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmt(totalContracts)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600">{fmt(totalAddendums)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmtM(totalCtrValue)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right" style={{ color: totalAdmValue < 0 ? '#DC2626' : '#D97706' }}>{fmtM(totalAdmValue)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-700">{fmtM(totalValue)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(totalWeight)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      <ContractDrillDownPanel
        open={drillOpen}
        title={drillTitle}
        baseParams={baseParams || {}}
        extraParams={drillExtra}
        onClose={() => setDrillOpen(false)}
      />
    </>
  );
};

ContractSalesRegionChart.propTypes = {
  data:        PropTypes.array,
  loading:     PropTypes.bool,
  error:       PropTypes.any,
  baseParams:  PropTypes.object,
  unitData:    PropTypes.array,
  unitLoading: PropTypes.bool,
};
ContractSalesRegionChart.defaultProps = { data: [], loading: false, error: null, baseParams: {}, unitData: [], unitLoading: false };

export default ContractSalesRegionChart;
